import { ClickAwayListener, Drawer, IconButton, Paper, styled, Theme } from '@ethings/ui-toolkit';
import { useTheme } from '@mui/material';
import { Box } from '@mui/system';
import { FC, useRef, useState } from 'react';
import { LayerSelectionProps, ToggleLayers, Undefinable } from '../../../types';
import '../../../types/theme.types';
import { disableClickPropagation } from '../../../util/maps';
import { Icon } from '../../icons';
import { MainMenuDrawer } from './main-menu-drawer/main-menu-drawer';
import { MainMenu } from './main-menu/main-menu';
import { baseLayers as defaultBaseLayers } from './map-layers-default';
import { SubMenu } from './sub-menu/sub-menu';

const StyledPaper = styled(Paper)(({ theme }: { theme: Theme }) => ({
  cursor: 'default',
  right: '72px',
  bottom: '24px',
  borderRadius: theme.shape.borderRadius * 2,
  minWidth: '200px',
  overflowX: 'hidden',
  padding: theme.spacing(1),
  position: 'absolute',
  transition: 'width 125ms ease, transform 125ms ease',
  zIndex: 500,
}));

export const SLIDE_DURATION = 200;
export const DRAWER_CONTAINER_ID = 'inlay-map-layer-selection-root';
export const MODAL_DRAWER_CONTAINER_ID = 'modal-inlay-map-layer-selection-root';

const LayerButton = styled(IconButton)(({ theme }) => ({
  backgroundColor: theme.palette.primary.contrastText,
  boxShadow: theme.shadows[3],
  '&:hover': {
    backgroundColor: theme.palette.grey['50'],
  },
}));

export const LayerSelection: FC<LayerSelectionProps> = ({
  activeLayers,
  handleBaseChange,
  handleToggleChange,
  mapBaseLayers,
  mapToggleLayers,
  open,
  selectedBaseLayer,
  selectedToggleLayers,
  setOpen,
  drawerLayerSelection = false,
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const theme = useTheme();
  const [selectedSubMenu, setSelectedSubMenu] = useState<ToggleLayers>();
  const [subMenuHeight, setSubMenuHeight] = useState(0);
  const [subMenuWidth, setSubMenuWidth] = useState(0);

  if (!mapBaseLayers.length && !mapToggleLayers.length) return <></>;

  const isLayerActive = (label: ToggleLayers): boolean => {
    return Boolean(activeLayers.find((layer) => layer.label === label));
  };
  const isLayerChecked = (label: ToggleLayers): boolean => selectedToggleLayers.includes(label);
  const openSubMenu = (label: Undefinable<ToggleLayers>): void => setSelectedSubMenu(label);

  const drawerContainer = document.getElementById(MODAL_DRAWER_CONTAINER_ID) ?? document.getElementById(DRAWER_CONTAINER_ID);
  const baseLayers = mapBaseLayers.map((item) => ({ ...item, label: item.label }));
  const previewLayer = defaultBaseLayers().find((item) => item.label === selectedBaseLayer) ?? baseLayers[0];
  const otherLayers = baseLayers.filter((item) => item.label !== selectedBaseLayer);

  disableClickPropagation(ref);

  return (
    <ClickAwayListener onClickAway={() => selectedSubMenu ? setSelectedSubMenu(undefined) : setOpen(false)}>
      {!drawerLayerSelection ?
        <StyledPaper
          ref={ref}
          elevation={3}
        >
          {selectedSubMenu
            ? <SubMenu
              closeSubMenu={() => setSelectedSubMenu(undefined)}
              height={subMenuHeight}
              width={subMenuWidth}
              selectedSubMenu={selectedSubMenu}
            />
            : <MainMenu
              handleBaseChange={handleBaseChange}
              handlePreviewLayerClick={() => setOpen(!open)}
              handleToggleChange={handleToggleChange}
              isBottomSectionIn={open}
              isLayerChecked={isLayerChecked}
              isLayerActive={isLayerActive}
              mapToggleLayers={mapToggleLayers}
              openSubMenu={openSubMenu}
              otherLayers={otherLayers}
              previewLayer={previewLayer}
              setSubMenuHeight={setSubMenuHeight}
              setSubMenuWidth={setSubMenuWidth}
              slideIn={!selectedSubMenu}
            />
          }
        </StyledPaper>
        : <Box>
          <Box bottom='116px' right={theme.spacing(2)} position='absolute' zIndex={800} >
            <LayerButton color='primary' data-testid="inlay-map-layers-button" onClick={() => setOpen(!open)}>
              <Icon data-testid="layers-icon" icon="Layers" />
            </LayerButton>
          </Box>
          <Box data-testid="inlay-map-layer-selection-root" id={DRAWER_CONTAINER_ID} sx={{ width: 1, height: 1, position: 'absolute', pointerEvents: open ? 'all' : 'none' }}>
            <Drawer
              ref={ref}
              anchor="right"
              open={open}
              onClose={() => setOpen(!open)}
              BackdropProps={{
                style: { position: 'absolute' },
              }}
              ModalProps={{
                container: drawerContainer,
                sx: { position: 'absolute' },
              }}
              PaperProps={{
                sx: {
                  py: 1,
                  px: 2,
                  position: 'absolute',
                  width: selectedSubMenu ? '100%' : 'fit-content',
                  bottom: 0,
                  cursor: 'default',
                  overflowX: 'hidden',
                  zIndex: 500,
                  '& .MuiRadio-root': { py: .5 },
                  scrollbarWidth: 'thin',
                  '&::-webkit-scrollbar': {
                    width: theme.scrollbar.width,
                    height: theme.scrollbar.height,
                    backgroundColor: theme.palette.common.white,
                  },
                  '&::-webkit-scrollbar-track': {
                    background: theme.palette.grey[300],
                    borderRadius: theme.scrollbar.borderRadius,
                  },
                  '&::-webkit-scrollbar-thumb': {
                    backgroundColor: theme.palette.primary.main,
                    borderRadius: theme.scrollbar.borderRadius,
                  },
                },
              }}
              SlideProps={{
                onEnter: (node) => {
                  node.style.transform = 'translateX(0%)';
                },
                onExiting: (node) => {
                  node.style.transform = 'translateX(100%)';
                },
              }}
            >
              {selectedSubMenu ?
                <SubMenu closeSubMenu={() => setSelectedSubMenu(undefined)} selectedSubMenu={selectedSubMenu} />
                :
                <MainMenuDrawer
                  handleBaseChange={handleBaseChange}
                  handlePreviewLayerClick={() => setOpen(!open)}
                  handleToggleChange={handleToggleChange}
                  isLayerChecked={isLayerChecked}
                  isLayerActive={isLayerActive}
                  mapToggleLayers={mapToggleLayers}
                  openSubMenu={openSubMenu}
                  previewLayer={previewLayer}
                  baseLayers={baseLayers} />
              }
            </Drawer>
          </Box>
        </Box>}
    </ClickAwayListener>
  );
};
